import {
  actions,
  persistor,
  store,
  slices,
  selectors,
  userAPI,
} from "./redux/store";
import { GoogleSigninResponse } from "./redux/services/api/types";

import "./components";
import {
  sendEventAddFavorite,
  sendEventLoggedUser,
  sendEventLogin,
  sendEventRegister,
} from "./utils/analytics";
import { AuthCookies } from "./utils/auth-cookies";
import {
  SessionController,
  SessionControllerConfiguration,
  SessionEvents,
  SessionSnapshot,
} from "../session-controller";
import { EnvironmentType, getEnvironmentType } from "../utils/environment";
import { TMNotificationSystem } from "../notification-system";
import { NotificationModelFactory } from "../notification-system/models/notification-model-factory";
import {
  AuthenticationBootstrappedEvent,
  UserStatusResponseEvent,
} from "./types";
import { doesNotNeedReloadRoute, isProtectedRoute } from "./utils/routes";
import { hideLoginModal } from "./utils/loginModal";
import {
  addAuthenticationBootstrappedEventListener,
  addUserLoggedEventListener,
  addUserLogoutEventListener,
  addUserStatusRequestEventListener,
} from "./utils/listeners";
import { getShouldShowRegisterModal } from "./utils/registerModal";

const SESSION_PING_TIME_INTERVAL = 360000; // 6 minutes
const SESSION_CONTROLLER_SETTINGS: SessionControllerConfiguration = {
  idleTimeBetweenSessions: 900000, // 15 minutes
  timeBetweenUpdates: 15000, // 15 seconds
  debug:
    getEnvironmentType() === EnvironmentType.DEVELOPMENT ||
    getEnvironmentType() === EnvironmentType.STAGING,
};
const TIME_TO_UPDATE_NOTIFICATIONS_SINCE_LAST_UPDATE = 300000; // 5 minutes

addAuthenticationBootstrappedEventListener((event) => {
  (window as any).tm_auth_bootstrapped = true;

  addUserStatusRequestEventListener((event) => {
    const state = store.getState();
    const authState = state[slices.auth.name];
    const isLogged = selectors.auth.isLogged(authState);
    const isPremium = selectors.auth.isPremium(authState);
    const uuid = selectors.auth.getUser(authState)?.uuid;

    window.dispatchEvent(
      new UserStatusResponseEvent({ uuid, isLogged, isPremium })
    );
  });
});

addUserLoggedEventListener((event) => {
  const { uuid } = event.detail;

  const pingSession = (snapshot: SessionSnapshot) => {
    const state = store.getState();
    const authState = state[slices.auth.name];
    const authData = selectors.auth.credentials(authState);
    store
      .dispatch(
        userAPI.endpoints.pingSession.initiate({
          ...authData,
          data: {
            initialTimeStamp: snapshot.initialTimeStamp,
            lastUpdatedTimeStamp: snapshot.lastUpdatedTimeStamp,
            timeSpent: snapshot.totalTimeSpent,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            currentPage: window.location.pathname,
          },
        })
      )
      .then(() => {
        SessionController.saveSnapshot("pinged", snapshot);
        store.dispatch(
          userAPI.endpoints.getPendingNotifications.initiate(authData)
        );
      });
  };

  let lastPingedSessionSnapshot: SessionSnapshot | null = null;
  SessionController.instance.addEventListener(SessionEvents.START, () => {
    const state = store.getState();
    const authState = state[slices.auth.name];
    const notificationsState = state[slices.notification.name];
    const authData = selectors.auth.credentials(authState);

    if (notificationsState.pendingNotificationsUpdateTimestamp) {
      notificationsState.pendingNotifications.forEach((notification) => {
        if (notification.delivered && notification.deliveredAt) return;
        if (notification.delivered && !notification.deliveredAt) {
          return store.dispatch(
            userAPI.endpoints.deliverNotification.initiate({
              ...authData,
              data: {
                notification: notification.id,
              },
            })
          );
        }

        const notificationModel = NotificationModelFactory.create({
          id: notification.id,
          type: notification.type,
          priority: notification.priority,
          title: notification.title,
          message: notification.message,
          data: notification.data,
        });

        if (!notificationModel) return;
        TMNotificationSystem.instance.enqueueNotification(notificationModel);
      });

      const timeSinceLastUpdate =
        Date.now() - notificationsState.pendingNotificationsUpdateTimestamp;
      if (timeSinceLastUpdate < TIME_TO_UPDATE_NOTIFICATIONS_SINCE_LAST_UPDATE)
        return;
    }

    store.dispatch(
      userAPI.endpoints.getPendingNotifications.initiate(authData)
    );
  });

  SessionController.instance.addEventListener(SessionEvents.UPDATE, (e) => {
    const snapshot: SessionSnapshot = (e as CustomEvent).detail;
    if (!lastPingedSessionSnapshot) {
      const savedSnapshot = SessionController.getSavedSnapshot("pinged");
      if (snapshot.id === savedSnapshot?.id) {
        const timeSpentSinceLastPing =
          snapshot.totalTimeSpent - savedSnapshot.totalTimeSpent;
        if (timeSpentSinceLastPing < SESSION_PING_TIME_INTERVAL) {
          lastPingedSessionSnapshot = savedSnapshot;
        }

        if (timeSpentSinceLastPing >= SESSION_PING_TIME_INTERVAL) {
          pingSession(snapshot);
          lastPingedSessionSnapshot = snapshot;
        }

        return;
      }

      if (snapshot.totalTimeSpent >= SESSION_PING_TIME_INTERVAL) {
        pingSession(snapshot);
      }

      lastPingedSessionSnapshot = snapshot;
      return;
    }

    if (
      snapshot.totalTimeSpent - lastPingedSessionSnapshot.totalTimeSpent >=
      SESSION_PING_TIME_INTERVAL
    ) {
      pingSession(snapshot);
      lastPingedSessionSnapshot = snapshot;
    }
  });

  SessionController.create({
    ...SESSION_CONTROLLER_SETTINGS,
    userId: uuid,
  });
});

addUserLogoutEventListener((event) => {
  const { isPremium } = event.detail;
  SessionController.delete();
  SessionController.deleteSavedSnapshot("pinged");
  store.dispatch(actions.notification.deleteAllNotifications());

  if (isPremium || isProtectedRoute(window.location.pathname)) {
    return window.location.reload();
  }
});

window.googleLoginHandler = async function (response: GoogleSigninResponse) {
  setupLoginHandler();
  store.dispatch(userAPI.endpoints.googleLogin.initiate(response));
};

function getLoginReferrer(forcedNextUrl: string = "") {
  const params = new URLSearchParams(window.location.search);
  const nextUrl = forcedNextUrl ? forcedNextUrl : params.get("next");
  const documentReferrer =
    document.referrer && document.referrer.startsWith(window.location.origin)
      ? document.referrer.replace(window.location.origin, "")
      : "";

  let referrer = nextUrl ? nextUrl : documentReferrer;

  if (referrer && !referrer.startsWith("/")) {
    referrer = "";
  }

  if (referrer && !referrer.endsWith("/")) {
    referrer += "/";
  }

  if (referrer) {
    if (referrer.includes("?")) {
      referrer += `&cb=${Date.now()}`;
    } else {
      referrer += `?cb=${Date.now()}`;
    }
  }

  return referrer;
}

function setupLoginHandler() {
  const unsubscribe = store.subscribe(async () => {
    const state = store.getState();
    if (selectors.auth.isLogged(state[slices.auth.name])) {
      unsubscribe();
      const authState = state[slices.auth.name];
      const uuid = selectors.auth.getUser(authState)!.uuid;
      const isNewUser = selectors.auth.isNewUser(authState);
      const isPremium = selectors.auth.isPremium(authState);

      // If the user is not a new user OR the register modal should not be shown, close the login modal
      if (!isNewUser || !getShouldShowRegisterModal()) {
        hideLoginModal();
      }

      sendEventLogin();
      sendEventLoggedUser({ uuid, isPremium });
      if (isNewUser) sendEventRegister();

      const params = new URLSearchParams(window.location.search);
      if (params.get("f")) {
        const favoriteId = params.get("f")!;
        const response = await store.dispatch(
          userAPI.endpoints.updateFavorite.initiate({
            ...selectors.auth.credentials(state[slices.auth.name]),
            content_id: favoriteId,
          })
        );

        if ("data" in response) {
          sendEventAddFavorite(favoriteId);
        }
      }

      if (window.location.pathname.includes("login")) {
        const referrer = getLoginReferrer();
        if (referrer) {
          return window.location.replace(referrer);
        }

        return window.location.replace(
          `${window.location.origin}/user/minhas-conquistas/`
        );
      }

      if (window.location.pathname.includes("checkout")) {
        return window.location.reload();
      }

      if (
        !doesNotNeedReloadRoute(window.location.pathname) &&
        selectors.auth.isPremium(authState)
      ) {
        return window.location.reload();
      }

      if (window.location.pathname.includes("exercicios")) {
        return window.location.reload();
      }
    }
  });
}

async function initialAuthValidation() {
  const state = store.getState();
  const authState = state[slices.auth.name];
  const isLogged = selectors.auth.isLogged(authState);
  const isLoggedUserCredentialsValid = AuthCookies.validate(isLogged);

  if (!isLoggedUserCredentialsValid) {
    store.dispatch(slices.auth.actions.logout());
  }

  if (isLogged && isLoggedUserCredentialsValid) {
    const response = await store.dispatch(
      userAPI.endpoints.validateToken.initiate(
        selectors.auth.credentials(authState)
      )
    );

    if (
      "error" in response &&
      (response.error as any).status !== "FETCH_ERROR"
    ) {
      window.dispatchEvent(new AuthenticationBootstrappedEvent());
      return store.dispatch(slices.auth.actions.logout());
    }

    const { uuid } = selectors.auth.credentials(authState);
    const isPremium = selectors.auth.isPremium(authState);
    sendEventLoggedUser({ uuid, isPremium });
  }

  window.dispatchEvent(new AuthenticationBootstrappedEvent());
}

const { bootstrapped } = persistor.getState();
if (!bootstrapped) {
  const unsubscribe = persistor.subscribe(() => {
    const { bootstrapped } = persistor.getState();
    if (bootstrapped) {
      unsubscribe?.call(this);
      initialAuthValidation();
    }
  });
}

if (bootstrapped) {
  initialAuthValidation();
}
